import type { Row } from "@rocicorp/zero";
import { useMemo } from "react";
import { emptyRouteSummary } from "../../../shared/monitoringRouteAuth";
import type {
  MonitoringUnitsMonthResponse,
  MonitoringUnitsSpaceRow,
  MonitoringUnitsWorkflowRow,
  MonitoringWorkflowDependenciesResponse,
  MonitoringWorkflowDependenciesRow,
  MonitoringWorkflowRoutesResponse,
  MonitoringWorkflowRoutesRow,
  MonitoringWorkflowsResponse,
} from "../../../shared/monitoringWorkflowsReport";
import { STORAGE_USAGE_REFRESH_INTERVAL_MS } from "../../../shared/monitoringWorkflowsReport";
import { queries } from "../../../shared/queries";
import { useQuery } from "@rocicorp/zero/react";
import { useRevisionedJson } from "../../hooks/useRevisionedJson";
import type { DisplayableSpace } from "../../hooks/useSpaceDisplayName";
import {
  monitoringUnitsMonthPath,
  monitoringWorkflowDependenciesPath,
  monitoringWorkflowRoutesPath,
  monitoringWorkflowsPath,
} from "../../lib/apiPaths";

const MONITOR_STATS_WINDOW_DAYS = 30;

const EMPTY_REPORT: MonitoringWorkflowsResponse = {
  workflows: [],
  failing: [],
  failureTrend: [],
  storage: [],
  storageUsage: null,
  storageSampledAt: null,
  units: null,
  unitsBySpace: null,
  unitsByWorkflow: null,
  unitsTrend: null,
  routeSummary: emptyRouteSummary(),
  dependencySummary: [],
};

// Panels must read through this hook (same path, same query string) so the tab
// makes one request instead of one per panel.
export function useMonitoringWorkflows({
  pollStorage = false,
}: { pollStorage?: boolean } = {}): {
  report: MonitoringWorkflowsResponse;
  loading: boolean;
} {
  const { data, loading } = useRevisionedJson<MonitoringWorkflowsResponse>(
    monitoringWorkflowsPath(),
    `days=${MONITOR_STATS_WINDOW_DAYS}`,
    undefined,
    pollStorage ? nextStorageRefreshAt : undefined
  );
  return { report: data ?? EMPTY_REPORT, loading };
}

// How soon to check again once a sample is due but hasn’t landed: a stack
// writes a sample only after measuring every tenant, which can take a minute.
// Rechecks back off to the refresh interval in case sampling has stalled.
const STORAGE_SAMPLE_RECHECK_MS = 15_000;

function nextStorageRefreshAt(
  report: MonitoringWorkflowsResponse,
  fetchedAt: number
) {
  if (report.storageUsage === null) return null;
  const due =
    (report.storageSampledAt ?? fetchedAt) + STORAGE_USAGE_REFRESH_INTERVAL_MS;
  if (fetchedAt < due) return due + STORAGE_SAMPLE_RECHECK_MS;
  return (
    fetchedAt +
    Math.min(
      Math.max(STORAGE_SAMPLE_RECHECK_MS, fetchedAt - due),
      STORAGE_USAGE_REFRESH_INTERVAL_MS
    )
  );
}

// The main report carries only the current month's per-space and per-workflow
// split; a past month opened in the drill-in loads its own breakdown here.
// `periodStart` is null for the current month (served from the main report) and
// when the drill-in is closed, so neither case fetches.
export function useMonitoringUnitsMonth(periodStart: number | null): {
  unitsBySpace: MonitoringUnitsSpaceRow[] | null;
  unitsByWorkflow: MonitoringUnitsWorkflowRow[] | null;
  loading: boolean;
} {
  const { data, loading } = useRevisionedJson<MonitoringUnitsMonthResponse>(
    monitoringUnitsMonthPath(),
    periodStart == null ? null : `month=${periodStart}`
  );
  return {
    unitsBySpace: data?.unitsBySpace ?? null,
    unitsByWorkflow: data?.unitsByWorkflow ?? null,
    loading,
  };
}

export function useMonitoringWorkflowRoutes(open: boolean): {
  workflows: MonitoringWorkflowRoutesRow[];
  loading: boolean;
} {
  const { data, loading } = useRevisionedJson<MonitoringWorkflowRoutesResponse>(
    monitoringWorkflowRoutesPath(),
    open ? "" : null
  );
  return { workflows: data?.workflows ?? [], loading };
}

export function useMonitoringWorkflowDependencies(open: boolean): {
  workflows: MonitoringWorkflowDependenciesRow[];
  loading: boolean;
} {
  const { data, loading } =
    useRevisionedJson<MonitoringWorkflowDependenciesResponse>(
      monitoringWorkflowDependenciesPath(),
      open ? "" : null
    );
  return { workflows: data?.workflows ?? [], loading };
}

// Personal-space owner names ride the personalSpaceOwners query, as spacesLite
// only relates the viewer's own grants.
export function useMonitoringSpaces(): {
  spaces: DisplayableSpace[];
  spaceById: Map<string, DisplayableSpace>;
  ownerBySpaceId: Map<string, Row["user"]>;
  loading: boolean;
} {
  const [spaceRows, spacesQuery] = useQuery(
    queries.spacesLite({ includeAdminSpaces: true })
  );
  const [personalSpaces] = useQuery(queries.personalSpaceOwners());
  const loading = spacesQuery.type !== "complete";

  return useMemo(() => {
    const ownerBySpaceId = new Map<string, Row["user"]>();
    for (const space of personalSpaces ?? []) {
      const owner = space.roleGrantsAsScope[0]?.user;
      if (owner) ownerBySpaceId.set(space.id, owner);
    }
    const ownerGrants = new Map(
      (personalSpaces ?? []).map((space) => [space.id, space.roleGrantsAsScope])
    );
    const spaces: DisplayableSpace[] = (spaceRows ?? []).map((space) => ({
      ...space,
      roleGrantsAsScope: ownerGrants.get(space.id) ?? [],
    }));
    return {
      spaces,
      spaceById: new Map(spaces.map((space) => [space.id, space])),
      ownerBySpaceId,
      loading,
    };
  }, [spaceRows, personalSpaces, loading]);
}
